import { useEffect, useState } from 'react';
import { Platform } from 'react-native';

import { useIsLargeScreen } from './use-is-large-screen';

/**
 * Detects whether the app is running in a web browser on a mobile/touch
 * device (phone or tablet), including standalone PWA mode.
 *
 * Distinct from `Platform.OS === 'web'` (which is just the bundle target)
 * and from `useIsLargeScreen` (which only measures width). We need all three
 * signals — web + coarse pointer + small viewport — to distinguish a phone
 * PWA from a touchscreen laptop.
 */
export const useIsMobileWeb = (): boolean => {
	const isLargeScreen = useIsLargeScreen();
	const [hasCoarsePointer, setHasCoarsePointer] = useState(false);

	useEffect(() => {
		if (Platform.OS !== 'web' || typeof window === 'undefined') return;

		const mq = window.matchMedia?.('(pointer: coarse)');
		const update = () => {
			const hasTouch = typeof navigator !== 'undefined'
				&& ('maxTouchPoints' in navigator ? navigator.maxTouchPoints > 0 : 'ontouchstart' in window);
			setHasCoarsePointer(Boolean(mq?.matches) || hasTouch);
		};
		update();

		mq?.addEventListener?.('change', update);
		return () => mq?.removeEventListener?.('change', update);
	}, []);

	if (Platform.OS !== 'web') return false;
	return hasCoarsePointer && !isLargeScreen;
};

export const useIsDesktopWeb = (): boolean => {
	const isMobileWeb = useIsMobileWeb();
	return Platform.OS === 'web' && !isMobileWeb;
};
